import React from 'react'
import PropTypes from 'prop-types'
import { useTransition } from 'react-spring'
import { Modal } from '@entrata/portals-common/lib/components'
import {
  portalReskinStyle,
  rootReskinStyle,
} from 'portal-modules/common/components/modals/rp-modal'
import {
  StyledReskinModalDiv,
  StyledReskinModalWrapper,
  StyledReskinModalButtonWrapper,
  StyledReskinModalButton,
  StyledReskinModalOutsideContent,
} from 'portal-modules/common/components/modals/atoms'
import { FullscreenLoader } from 'portal-modules/common/components/loaders'
import SystemMessage from 'portal-modules/common/components/SystemMessage'
import getProjectConditions from 'portal-modules/common/utils/get-project-conditions'
const { isRP } = getProjectConditions()

const ReskinModal = ({
  blurId,
  hideCloseButton,
  onClose,
  onClick,
  rootId,
  show,
  content,
  children,
  isFormActionRequired,
  numberOfButtons,
  actionButtonType,
  actionButtonLabel,
  closeButtonType,
  closeButtonLabel,
  loadingMsg,
  error,
  isWideDialog,
  outsideContent,
  modalId,
  modalOverlay,
  customHeight,
  customZIndex = '5',
  customWidth,
  hasBackground = false,
}) => {
  const toggleRoot = action => {
    const rootElement = document.getElementById(rootId)
    const blurElement = document.getElementById(blurId)
    if (action === 'add' && modalId) {
      rootElement && rootElement.classList[action](rootReskinStyle)
      blurElement && blurElement.classList[action]('blur')
      blurElement && blurElement.classList[action](modalId)
    } else {
      if (action === 'remove' && blurElement.classList.contains(modalId)) {
        rootElement && rootElement.classList[action](rootReskinStyle)
        !modalOverlay && blurElement && blurElement.classList[action]('blur')
        blurElement && blurElement.classList[action](modalId)
      }
    }
  }
  React.useEffect(() => {
    modalOverlay ? toggleRoot('add') : toggleRoot(show ? 'add' : 'remove')
    return function removeReskinRootStyles() {
      toggleRoot('remove')
    }
  }, [show])

  const transitions = useTransition(show, null, {
    from: { opacity: 0, transform: 'translateY(100px)' },
    enter: { opacity: 1, transform: 'translateY(0px)' },
    leave: { opacity: 0, transform: 'translateY(-200px)' },
  })

  return transitions.map(
    ({ item, key, props }) =>
      item && (
        <Modal
          className={portalReskinStyle(customZIndex)}
          key={key}
          onClose={onClose}
          containerAttributes={{id: modalId}}
        >
          {({ containerProps }) => (
            <>
              <StyledReskinModalWrapper
                style={{ ...props }}
                isWideDialog={isWideDialog}
                customWidth={customWidth}
              >
                <StyledReskinModalDiv
                  className={!hideCloseButton ? 'remove-pad' : ''}
                  {...containerProps}
                  isWideDialog={isWideDialog}
                  customHeight={customHeight}
                  customWidth={customWidth}
                  hasBackground={hasBackground}
                >
                  {!!error && (
                    <>
                      <br />
                      <SystemMessage type='error' shouldScrollOnMount>
                        {error}
                      </SystemMessage>
                    </>
                  )}
                  {!isFormActionRequired ? (
                    <>
                      {content}
                      {isWideDialog && (
                        <StyledReskinModalButtonWrapper>
                          {numberOfButtons == 1 ? (
                            <StyledReskinModalButton
                              appearance={actionButtonType}
                              size='medium'
                              onClick={onClick}
                            >
                              {__(actionButtonLabel)}
                            </StyledReskinModalButton>
                          ) : (
                            <>
                              <StyledReskinModalButton
                                appearance={actionButtonType}
                                size='medium'
                                onClick={onClick}
                              >
                                {__(actionButtonLabel)}
                              </StyledReskinModalButton>
                              <StyledReskinModalButton
                                appearance={closeButtonType}
                                size='medium'
                                onClick={onClose}
                              >
                                {__(closeButtonLabel)}
                              </StyledReskinModalButton>
                            </>
                          )}
                        </StyledReskinModalButtonWrapper>
                      )}
                    </>
                  ) : (
                    children
                  )}
                  <FullscreenLoader show={!!loadingMsg}>
                    {loadingMsg}
                  </FullscreenLoader>
                </StyledReskinModalDiv>
                <StyledReskinModalOutsideContent onClick={() => onClick(true)}>
                  {outsideContent}
                </StyledReskinModalOutsideContent>
              </StyledReskinModalWrapper>
            </>
          )}
        </Modal>
      ),
  )
}

ReskinModal.propTypes = {
  /** ID of the background to be blurred  */
  blurId: PropTypes.string,
  /** Custom text for close button */
  buttonLabel: PropTypes.string,
  /** You can directly pass some string content to show to the users */
  children: PropTypes.any,
  /** To toggle visibility of Close button */
  hideCloseButton: PropTypes.bool,
  /** onClose method, provided by the modal control */
  onClose: PropTypes.func.isRequired,
  /** ID of the root element */
  rootId: PropTypes.string,
  /** A flas to show/hide modal. Provided by modal control */
  show: PropTypes.bool,
  /** Title of the modal  */
  title: PropTypes.string,
  /** Title of the modal  */
  isWideDialog: PropTypes.bool,
  subtitle: PropTypes.string,
  numberOfButtons: PropTypes.number,
  buttonLabels: PropTypes.arrayOf(PropTypes.string),
  buttonTypes: PropTypes.arrayOf(PropTypes.string),
  loadingMsg: PropTypes.string,
  error: PropTypes.string,
  isFormActionRequired: PropTypes.bool,
  modalId: PropTypes.string.isRequired,
  customHeight: PropTypes.string,
  customZIndex: PropTypes.string,
}

ReskinModal.defaultProps = {
  blurId: isRP ? 'bg-overlay' : 'bg-img',
  buttonLabel: __('Close'),
  rootId: 'root',
  subtitle: '',
  actionButtonType: 'primary',
  actionButtonLabel: __('Close'),
  closeButtonType: 'secondary',
  closeButtonLabel: __('Close'),
  numberOfButtons: 1,
  error: '',
  isFormActionRequired: false,
  isWideDialog: true,
  customHeight: '',
  customZIndex: '',
}

export default ReskinModal
